/**
 * RespawnResolver — pure target resolution for a fallen/failed cat.
 *
 * Decides WHERE a cat respawns; writes nothing. Callers feed candidate
 * positions and consume the returned {pos, strategy}, then route the actual
 * teleport through respawnCharacter (BaseLevel.ts) and emit PLAYER_RESPAWNED.
 *
 * No Babylon imports: operates on plain {x,y,z} so it is fully unit-testable
 * under the NullEngine/node Vitest env without a scene.
 *
 * Navigator safety: qaActive ALWAYS forces a deterministic checkpoint/spawn
 * target (never the partner), so the QA CatNavigator stays reproducible.
 */

export type RespawnStrategy = "partner" | "checkpoint" | "spawn";

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export interface RespawnInput {
  fallenCatId: "minna" | "basil";
  isCoop: boolean;
  qaActive: boolean;
  partnerSafe: boolean;
  partnerPos: Vec3 | null;
  checkpointPos: Vec3 | null;
  levelSpawnPos: Vec3;
}

export interface RespawnResult {
  pos: Vec3;
  strategy: RespawnStrategy;
}

/**
 * Resolve the respawn target. Rules, in strict order:
 *   1. !qaActive && isCoop && partnerSafe && partnerPos -> partner
 *   2. else if checkpointPos                            -> checkpoint
 *   3. else                                             -> spawn
 */
export function resolveRespawn(i: RespawnInput): RespawnResult {
  if (!i.qaActive && i.isCoop && i.partnerSafe && i.partnerPos) {
    return { pos: i.partnerPos, strategy: "partner" };
  }
  if (i.checkpointPos) {
    return { pos: i.checkpointPos, strategy: "checkpoint" };
  }
  return { pos: i.levelSpawnPos, strategy: "spawn" };
}
